import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { isStaff } from "../../utils/roles";
import AwaitingApproval from "../pages/AwaitingApproval";

/**
 * Replaces the whole app with an explanation when the signed-in account is not
 * approved staff.
 *
 * A pending applicant genuinely holds a session — they have to, or their
 * profile could never be created and they would never reach the approval queue
 * (see accessPolicy.decideSignIn on the server). What they do not hold is any
 * privilege: the server resolves them to 'customer' and refuses every staff
 * route. Without this guard that would surface as a booking screen where
 * nothing works.
 *
 * This is presentation, not enforcement. Removing it in a browser reveals a UI
 * whose every API call is still refused.
 *
 * Sits inside EmailVerificationGuard, so someone verifies their address first
 * and only then learns they are waiting — which is the agreed order.
 */
const EXEMPT_PATHS = new Set(["/login", "/signup"]);

const BLOCKED_STATUSES = new Set(["pending", "rejected", "disabled"]);

const StaffApprovalGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, profile, loading } = useAuth();
    const location = useLocation();

    // Don't flash the screen while auth or the profile is still resolving.
    if (loading || !user || !profile) return <>{children}</>;
    if (EXEMPT_PATHS.has(location.pathname)) return <>{children}</>;

    // Approved staff, super users and IT carry on as normal.
    if (isStaff(profile)) return <>{children}</>;

    if (BLOCKED_STATUSES.has(profile.staffStatus)) {
        return <AwaitingApproval reason={profile.staffStatus} />;
    }

    // An ordinary customer. Phase 1 keeps the public out at the server, so
    // reaching here means public access was deliberately reopened — let them
    // through and leave the customer experience alone.
    return <>{children}</>;
};

export default StaffApprovalGuard;
